<template>
	<!-- Tant qu'il s'agit de choisir, l'écran se lit en colonne étroite : « Rejeter » doit rester à côté
	     de « Rapprocher ». Une fois le document déposé, il occupe toute la largeur pour se lire en
	     regard du mouvement qu'il justifie. -->
	<div class="row bank-flow-assign" :class="{ 'bank-flow-assign--deposited': deposited }">
		<div class="col-12" :class="{ 'col-xl-5': deposited }">
			<!-- Le mouvement tel que la banque le rapporte. Il est rappelé en entier parce que c'est lui,
			     et lui seul, qui permet de reconnaître la dépense : le libellé porte souvent le nom du
			     commerçant là où le montant ne dit rien. -->
			<div class="bank-flow-assign__header">
				<h3>{{ amount_format }}</h3>
				<!-- Surligner une partie du libellé la reprend dans la recherche : c'est là qu'apparaît le
			     nom du commerçant ou une référence de facture, et l'utilisateur l'a sous les yeux. -->
			<p class="mb-1" @mouseup="captureSelection">{{ bridge_transaction.bridgetransaction_description }}</p>
				<div class="bank-flow-assign__label">{{ date_format }}</div>
				<div class="mt-2">
					<img v-if="bank_logo_url" :src="bank_logo_url" class="mr-1" style="height: 20px;">{{ bank_name }}
				</div>
			</div>

			<h4>{{ $t('invoice.bank_flow_justificatif') }}</h4>

			<!-- Une facture vient d'être déposée : elle a réglé le mouvement, et il ne reste qu'à la
			     contrôler. Les propositions n'ont plus d'objet. -->
			<div v-if="deposited">
				<p>{{ $t('invoice.bank_flow_supplier_deposited') }}</p>

				<div v-if="deposited.suppliersocr_ocr_status === 'waiting'" class="bank-flow-assign__label">
					<font-awesome-icon :icon="['fal', 'spinner']" spin /> {{ $t('invoice.bank_flow_supplier_ocr_waiting') }}
				</div>
				<router-link v-else :to="{ name: 'invoiceSupplierFicheControl', params: { invoicesupplier_id: deposited.suppliersocr_id } }">
					{{ $t('invoice.bank_flow_supplier_control') }} <font-awesome-icon :icon="['fal', 'external-link']" />
				</router-link>
			</div>

			<LoadingSpinner v-else-if="loading" />

			<template v-else>
				<!-- La proposition automatique s'en tient au montant exact : un règlement partiel, un
				     acompte ou un virement groupé n'y figure jamais. La recherche les rattrape, par le
				     nom du fournisseur ou le numéro du document. -->
				<form class="form-group" @submit.prevent="search">
					<div class="input-group">
						<input type="text" class="form-control" v-model="search_value" :placeholder="$t('invoice.bank_flow_supplier_search')">
						<div class="input-group-append">
							<button class="btn btn-secondary" type="submit" :disabled="searching">
								<font-awesome-icon v-if="searching" :icon="['fal', 'spinner']" spin />
								<font-awesome-icon v-else :icon="['fal', 'search']" />
							</button>
						</div>
					</div>
				</form>

				<!-- Panier d'affectation : les pièces retenues survivent aux recherches successives. Sans
				     lui, chercher la deuxième facture faisait perdre la première. -->
				<div v-if="basket.length" class="bank-flow-assign__basket">
					<div class="bank-flow-assign__count">{{ $t('invoice.bank_flow_basket', { count: basket.length }) }}</div>

					<div v-for="item in basket" :key="'basket_'+item.suppliersocr_id" class="bank-flow-assign__card">
						<div class="d-flex align-items-start">
							<div class="flex-grow-1">
								<div class="font-weight-bold">{{ item.tier ? item.tier.tiers_rs : '' }} - {{ item.suppliersocr_num }}</div>
								<div class="bank-flow-assign__label">
									{{ $t('invoice.invoice_balance') }} : {{ item.balance_format }}
									<!-- Facture libellée dans une autre devise : le solde ci-dessus est sa
									     contre-valeur, celui du document est rappelé pour la reconnaître. -->
									<span v-if="item.balance_src_format" class="text-muted">({{ item.balance_src_format }})</span>
								</div>

								<!-- Montant imputé à cette facture : la somme des pièces retenues dépasse
								     souvent le mouvement, c'est l'opérateur qui dit ce qui va où. -->
								<div class="input-group input-group-sm mt-1">
									<input
										type="text"
										class="form-control"
										:value="allocations[item.suppliersocr_id]"
										@input="setAllocation(item, $event.target.value)"
									>
									<div class="input-group-append">
										<span class="input-group-text">{{ currency_symbol }}</span>
									</div>
								</div>

								<!-- Le montant imputé ne couvre pas la dette au centime : sur une facture en
								     devise, c'est le cours qui a bougé entre son émission et son règlement.
								     Personne d'autre que l'opérateur ne peut dire si c'est un écart de change
								     ou un paiement partiel, alors on le lui demande. -->
								<b-form-checkbox
									v-if="settlement_gaps[item.suppliersocr_id]"
									class="mt-1"
									:checked="!!settled[item.suppliersocr_id]"
									@change="setSettled(item, $event)"
								>
									<span class="bank-flow-assign__label">
										{{ $t('invoice.bank_flow_settle_exchange_gap', { gap: settlement_gaps[item.suppliersocr_id] }) }}
									</span>
								</b-form-checkbox>
							</div>
							<button class="btn btn-link p-0 ml-2" :title="$t('invoice.bank_flow_basket_remove')" @click="toggleBasket(item)">
								<font-awesome-icon :icon="['far', 'times']" />
							</button>
						</div>
					</div>

					<div class="bank-flow-assign__count" :class="{ 'text-danger': !allocation_balanced }">
						{{ $t('invoice.bank_flow_basket_allocated') }} : {{ allocated_format }} / {{ amount_format }}
					</div>

					<button class="btn btn-primary btn-block" :disabled="!can_save" @click="assignBasket">
						{{ $t('global.rapprocher') }}
						<font-awesome-icon v-if="processing === 'basket'" :icon="['fal', 'spinner']" spin />
					</button>
				</div>

				<div v-if="search_results" class="bank-flow-assign__count">
					{{ $t('invoice.bank_flow_supplier_search_result', { count: search_results.length }) }}
					<a href="" class="ml-1" @click.prevent="clearSearch">{{ $t('invoice.bank_flow_supplier_search_clear') }}</a>
				</div>
				<div v-else-if="visible_invoices.length" class="bank-flow-assign__count">
					{{ $t('invoice.bank_flow_supplier_suggestion_invoice', { count: visible_invoices.length }) }}
				</div>

				<div
					v-for="invoice in displayed_invoices"
					:key="'invoice_'+invoice.suppliersocr_id"
					class="bank-flow-assign__card"
				>
					<div class="font-weight-bold">
						<router-link v-if="invoice.tier" :to="{ name: 'tiersFiche', params: { tiers_id: invoice.tier.tiers_id }}">{{ invoice.tier.tiers_rs }}</router-link>
						<template v-if="invoice.tier"> - </template>
						<a href="" @click.prevent="quickPreview(invoice.suppliersocr_pdf_url)">
							{{ invoice.suppliersocr_num }} <font-awesome-icon :icon="['fas', 'eye']" />
						</a>
					</div>
					<div class="bank-flow-assign__label">{{ $t('invoice.bank_flow_supplier_emitted_on', { date: formatDate(invoice.suppliersocr_date) }) }}</div>
					<div class="font-weight-bold">
						{{ invoice.balance_format }}
						<span v-if="invoice.balance_src_format" class="text-muted font-weight-normal">({{ invoice.balance_src_format }})</span>
					</div>

					<!-- Le règlement s'impute sur un compte fournisseur : sans émetteur identifié, il n'y a
					     pas d'écriture à écrire, et c'est la fiche de la facture qui sert à le désigner. -->
					<div v-if="!invoice.tier" class="bank-flow-assign__label mt-1">
						{{ $t('invoice.bank_flow_supplier_tiers_required') }}
						<router-link :to="{ name: 'invoiceSupplierFicheControl', params: { invoicesupplier_id: invoice.suppliersocr_id } }">
							{{ $t('invoice.bank_flow_supplier_control') }}
						</router-link>
					</div>
					<!-- Un mouvement plus gros que le reste dû paie autre chose que cette facture : le
					     surplus partirait en compte courant du fournisseur sans que rien ne le dise. -->
					<div v-else-if="!search_results && !canAssign(invoice)" class="bank-flow-assign__label mt-1">
						{{ $t('invoice.bank_flow_supplier_balance_too_low') }}
					</div>

					<!-- Après une recherche, la pièce se met au panier : plusieurs factures peuvent se
					     partager un même mouvement, et le montant exact ne les désigne plus. Une
					     proposition, elle, tombe déjà juste — un clic suffit. -->
					<div v-if="search_results" class="mt-2">
						<b-form-checkbox
							name="invoices"
							:checked="inBasket(invoice)"
							:disabled="!invoice.tier"
							@change="toggleBasket(invoice)"
						>{{ $t('invoice.bank_flow_basket_add') }}</b-form-checkbox>
					</div>

					<div v-else class="row no-gutters mt-2">
						<div class="col-6 pr-1">
							<button class="btn btn-secondary btn-block" @click="reject('invoices', invoice.suppliersocr_id)">{{ $t('invoice.bank_flow_supplier_reject') }}</button>
						</div>
						<div class="col-6 pl-1">
							<button class="btn btn-primary btn-block" :disabled="!canAssign(invoice)" @click="assignInvoice(invoice)">
								{{ $t('global.rapprocher') }}
								<font-awesome-icon v-if="processing === 'invoice_'+invoice.suppliersocr_id" :icon="['fal', 'spinner']" spin />
							</button>
						</div>
					</div>
				</div>

				<template v-if="!search_results">
					<div v-if="visible_payments.length" class="bank-flow-assign__count">
						{{ $t('invoice.bank_flow_supplier_suggestion_payment', { count: visible_payments.length }) }}
					</div>

					<!-- Un règlement déjà saisi n'attend que son mouvement : le rapprocher ne touche pas au
					     solde de ses factures, qui est tombé le jour de sa saisie. -->
					<div
						v-for="payment in visible_payments"
						:key="'payment_'+payment.ispayment_id"
						class="bank-flow-assign__card"
					>
						<div class="font-weight-bold">
						<router-link v-if="payment.tiers" :to="{ name: 'tiersFiche', params: { tiers_id: payment.tiers.tiers_id }}">{{ payment.tiers.tiers_rs }}</router-link>
					</div>
						<div class="bank-flow-assign__label">
							{{ $t('invoice.bank_flow_supplier_paid_on', { date: payment.ispayment_date_format }) }}
							<template v-if="payment.method"> - {{ payment.method.paymentmethod_label }}</template>
						</div>
						<div v-for="invoice in payment.invoices" :key="invoice.suppliersocr_id">
							{{ invoice.suppliersocr_num }}
						</div>
						<div class="font-weight-bold">{{ payment.amount_format }}</div>

						<div class="row no-gutters mt-2">
							<div class="col-6 pr-1">
								<button class="btn btn-secondary btn-block" @click="reject('payments', payment.ispayment_id)">{{ $t('invoice.bank_flow_supplier_reject') }}</button>
							</div>
							<div class="col-6 pl-1">
								<button class="btn btn-primary btn-block" :disabled="!!processing" @click="assignPayment(payment)">
									{{ $t('global.rapprocher') }}
									<font-awesome-icon v-if="processing === 'payment_'+payment.ispayment_id" :icon="['fal', 'spinner']" spin />
								</button>
							</div>
						</div>
					</div>
				</template>

				<!-- Le compte de résultats dit déjà « 0 facture » pendant une recherche : ce message-ci ne
				     vaut que pour l'absence de proposition. -->
				<p v-if="!search_results && !displayed_invoices.length && !visible_payments.length">{{ $t('invoice.bank_flow_supplier_no_suggestion') }}</p>

				<div class="bank-flow-assign__separator">{{ $t('global.ou') }}</div>

				<!-- Dépôt du justificatif quand aucune écriture ne l'attend : la facture est créée, réglée
				     par ce mouvement, et sa lecture optique reprendra les montants ensuite. -->
				<vueDropzone
					v-if="dropzone_loaded"
					ref="dropzone"
					id="bank_flow_supplier_dropzone"
					:options="dropzoneOptions"
					:useCustomSlot="true"
					@vdropzone-success="onUploaded"
					@vdropzone-error="onUploadError"
				>
					<font-awesome-icon class="bank-flow-assign__dropzone-icon" :icon="['fal', 'upload']" />
					<div>{{ $t('invoice.bank_flow_supplier_dropzone') }}</div>
				</vueDropzone>
			</template>
		</div>

		<div v-if="deposited" class="col-12 col-xl-7">
			<iframe :src="deposited.suppliersocr_pdf_url" class="bank-flow-assign__preview"></iframe>
		</div>

		<b-modal size="xl" ref="modelPreview" hide-footer @hidden="pdf_url = null">
			<template v-slot:modal-title>
				{{ $t("action.previsualiser") }}
			</template>

			<iframe v-if="pdf_url != null" :src="pdf_url" height="1000px" width="100%" style="position:relative"></iframe>
			<div v-else>
				<LoadingSpinner />
			</div>
		</b-modal>
	</div>
</template>


<script type="text/javascript">
	import Constants from 'Constants'
	import Invoice from "@/mixins/Invoice.js"
	import OpenBank from "@/mixins/OpenBank.js"
	import Payment from "@/mixins/Payment.js"
	import vue2Dropzone from 'vue2-dropzone'
	import 'vue2-dropzone/dist/vue2Dropzone.min.css'

	export default {
		name: 'AssignationTransactionSupplier',
		mixins: [Invoice, OpenBank, Payment],
		props: {
			bridge_transaction: { type: Object, required: true },
			// Extrait du libellé surligné dans la liste avant d'ouvrir le rapprochement.
			initial_search: { type: String, default: '' }
		},

		data() {
			return {
				amount_format: '',
				date_format: '',
				loading: false,
				processing: null,
				invoices: [],
				payments: [],
				search_value: '',
				searching: false,
				// Résultats de la dernière recherche. null tant qu'aucune n'a été lancée : l'écran montre
				// alors ses propositions, et les deux listes ne se disputent jamais la place.
				search_results: null,
				// Pièces retenues pour ce mouvement, et la part imputée à chacune. Tenues à part des
				// résultats, qu'une recherche remplace : la sélection doit traverser les recherches.
				basket: [],
				allocations: {},
				settled: {},
				settlement_gaps: {},
				allocated_format: '',
				// Propositions écartées à l'écran : l'utilisateur dit qu'elles ne correspondent pas, et
				// elles cessent de l'encombrer le temps qu'il traite ce mouvement.
				rejected: { invoices: [], payments: [] },
				// Facture déposée à l'instant, dont on guette la lecture optique.
				deposited: null,
				ocr_timer: null,
				ocr_delay: 3000,
				ocr_watch_started_at: null,
				pdf_url: null,
				dropzone_loaded: false,
				dropzoneOptions: {
					url: '',
					thumbnailWidth: 150,
					acceptedFiles: 'image/*,application/pdf',
					maxFiles: 1,
					uploadMultiple: false,
					headers: {
						"Authorization": "Bearer " + this.getConfig('token'),
						withCredentials: true
					}
				}
			}
		},

		mounted() {
			this.init_component()
		},

		beforeDestroy() {
			clearTimeout(this.ocr_timer)
		},

		computed: {
			bank_name() {
				const account = this.bridge_transaction.bridge_account
				return account ? account.bridge_bank.bridgebank_name + ' - ' + account.bridgeaccount_name : ''
			},

			bank_logo_url() {
				const account = this.bridge_transaction.bridge_account
				return account ? account.bridge_bank.bridgebank_logo_url : null
			},

			/**
			 * Montant du mouvement en centimes, dans la convention du domaine : positif pour ce qu'on
			 * doit à un fournisseur, négatif pour un avoir qu'il nous rembourse. La banque compte à
			 * l'envers, d'où l'inversion du signe.
			 */
			amount_cents() {
				return Math.round(this.bridge_transaction.bridgetransaction_amount * -100)
			},

			currency_symbol() {
				return this.bridge_transaction.bridgetransaction_currency === 'EUR' ? '€' : this.bridge_transaction.bridgetransaction_currency
			},

			allocated_cents() {
				return this.basket.reduce((total, item) => {
					return total + Math.round(this.parseAmount(this.allocations[item.suppliersocr_id]) * 100)
				}, 0)
			},

			allocation_balanced() {
				return this.allocated_cents === this.amount_cents
			},

			can_save() {
				return this.basket.length > 0 && !this.processing && this.allocation_balanced
			},

			visible_invoices() {
				return this.invoices.filter(invoice => !this.rejected.invoices.includes(invoice.suppliersocr_id))
			},

			displayed_invoices() {
				return this.search_results || this.visible_invoices
			},

			visible_payments() {
				return this.payments.filter(payment => !this.rejected.payments.includes(payment.ispayment_id))
			}
		},

		methods: {
			async init_component() {
				// Le montant garde le signe de la banque : sur cet écran un débit règle une facture et un
				// encaissement solde un avoir, et l'effacer rendrait les deux indiscernables.
				this.amount_format = await this.priceFormat(this.bridge_transaction.bridgetransaction_amount, this.bridge_transaction.bridgetransaction_currency, true)
				this.date_format = this.formatDate(this.bridge_transaction.bridgetransaction_date)

				this.dropzoneOptions.url = this.constructRoute(Constants.BRIDGE_TRANSACTION_SUPPLIER_INVOICE_URL, {
					transaction_id: this.bridge_transaction.bridgetransaction_id
				}) + "?licence_key=" + Constants.USER_LICENCE_KEY
				this.dropzone_loaded = true

				await this.loadSuggestions()

				// Le surlignage fait dans la liste vaut demande de recherche : l'utilisateur a désigné
				// ce qu'il cherchait avant même d'arriver ici.
				if(this.initial_search) {
					this.search_value = this.initial_search
					this.search()
				}
			},

			async loadSuggestions() {
				this.loading = true

				// Le mixin ramène null quand l'appel échoue : l'écran reste alors sur une liste vide
				// plutôt que de casser sur des propositions qui ne sont jamais arrivées.
				const suggestions = await this.getSupplierSuggestionsByBridgeTransaction(this.bridge_transaction.bridgetransaction_id) || { invoices: [], payments: [] }

				const invoices = await this.formatInvoices(suggestions.invoices)

				const payments = suggestions.payments
				for(const payment of payments) {
					// Un décaissement est comptabilisé dans la devise de tenue, jamais dans celle du
					// fournisseur : le montant a déjà été converti, l'afficher sous le signe du dollar le
					// donnerait pour ce que la facture portait.
					payment.amount_format = await this.priceFormat(payment.ispayment_amount, payment.ledger_currency, true)
				}

				this.invoices = invoices
				this.payments = payments
				this.loading = false
			},

			/**
			 * Cherche une facture encore due par fournisseur ou par numéro. Les propositions cèdent la
			 * place aux résultats : une seule liste à l'écran, et le lien de retour les ramène.
			 */
			async search() {
				if(!this.search_value.trim()) {
					this.clearSearch()
					return
				}

				this.searching = true
				const invoices = await this.searchSupplierInvoicesByBridgeTransaction(this.bridge_transaction.bridgetransaction_id, this.search_value) || []
				this.search_results = await this.formatInvoices(invoices)
				this.searching = false
			},

			/**
			 * Revient aux propositions. Le panier se vide avec la recherche : les propositions se
			 * rapprochent d'un clic, et laisser une sélection en attente derrière elles ferait perdre
			 * silencieusement ce qui y avait été retenu.
			 */
			clearSearch() {
				this.search_value = ''
				this.search_results = null
				this.basket = []
				this.allocations = {}
				this.allocated_format = ''
			},

			inBasket(item) {
				return this.basket.some(retained => retained.suppliersocr_id === item.suppliersocr_id)
			},

			/**
			 * Retient ou retire une facture du panier. Une facture sans fournisseur identifié n'y entre
			 * pas : le règlement s'impute sur un compte fournisseur, il n'y aurait rien à écrire.
			 *
			 * @param {Object} item
			 */
			toggleBasket(item) {
				if(this.inBasket(item)) {
					this.basket = this.basket.filter(retained => retained.suppliersocr_id !== item.suppliersocr_id)
					this.$delete(this.allocations, item.suppliersocr_id)
					this.$delete(this.settled, item.suppliersocr_id)
					this.$delete(this.settlement_gaps, item.suppliersocr_id)
				}
				else {
					this.basket.push(item)
					// La facture entre avec ce qu'il reste à imputer, plafonné à ce qu'elle doit : c'est
					// la répartition attendue neuf fois sur dix. Une facture en devise, elle, se solde
					// rarement au centime : le plafond lui laisse l'écart qu'on admet, faute de quoi la
					// répartition tomberait juste à l'écran sans que le mouvement soit imputé en entier, et
					// la question de l'écart de change ne serait jamais posée.
					// Le signe est celui de la pièce, pas celui du reste à imputer : un avoir s'impute en
					// négatif, même glissé dans un panier dont le reste est positif — c'est tout l'intérêt
					// de le mettre là, il vient en déduction des factures qui l'accompagnent. Prendre le
					// signe du reste lui proposait une imputation positive, qui lui ajoutait de la dette.
					const remaining = this.amount_cents - this.allocated_cents
					const cap = Math.abs(item.suppliersocr_balance) + this.settlementTolerance(item.suppliersocr_balance, item.suppliersocr_balance_src !== null)
					const proposed = Math.min(Math.abs(remaining), cap) * Math.sign(item.suppliersocr_balance || remaining || 1)
					this.$set(this.allocations, item.suppliersocr_id, (proposed / 100).toFixed(2))
					this.refreshSettlementGap(item)
				}

				this.formatAllocated()
			},

			setAllocation(item, value) {
				this.$set(this.allocations, item.suppliersocr_id, value)
				this.formatAllocated()
				this.refreshSettlementGap(item)
			},

			setSettled(item, checked) {
				this.$set(this.settled, item.suppliersocr_id, checked)
			},

			/**
			 * Recalcule l'écart entre ce qui est imputé à une facture et ce qu'elle doit, et décide s'il
			 * mérite d'être soumis à l'opérateur.
			 *
			 * La question n'a de sens que sur une facture en devise — ailleurs, un écart est un paiement
			 * partiel, pas un cours qui a bougé — et que si l'écart reste petit : au-delà, le mouvement
			 * paie visiblement autre chose, et le proposer comme un écart de change inviterait à solder
			 * une facture qui ne l'est pas. Le seuil reprend celui du serveur, qui refuse au-delà.
			 */
			async refreshSettlementGap(item) {
				const gap = Math.round(this.parseAmount(this.allocations[item.suppliersocr_id]) * 100) - item.suppliersocr_balance
				const tolerance = this.settlementTolerance(item.suppliersocr_balance, item.suppliersocr_balance_src !== null)

				if(gap === 0 || Math.abs(gap) > tolerance) {
					this.$set(this.settlement_gaps, item.suppliersocr_id, null)
					this.$set(this.settled, item.suppliersocr_id, false)
					return
				}

				this.$set(this.settlement_gaps, item.suppliersocr_id, await this.priceFormat(gap / 100, item.suppliersocr_ledger_currency, true))
			},

			async formatAllocated() {
				this.allocated_format = await this.priceFormat(this.allocated_cents / 100, this.bridge_transaction.bridgetransaction_currency, true)
			},

			/**
			 * Rapproche le mouvement des factures du panier, chacune pour la part qui lui a été donnée.
			 */
			async assignBasket() {
				const allocations = {}
				this.basket.forEach(item => {
					allocations[item.suppliersocr_id] = Math.round(this.parseAmount(this.allocations[item.suppliersocr_id]) * 100)
				})

				this.processing = 'basket'
				await this.assignSupplierBridgeTransaction(
					this.bridge_transaction.bridgetransaction_id,
					this.basket.map(item => item.suppliersocr_id),
					null,
					allocations,
					this.basket.filter(item => this.settled[item.suppliersocr_id]).map(item => item.suppliersocr_id)
				)
				this.processing = null

				this.done()
			},

			/**
			 * Prépare les montants d'une liste de factures pour l'affichage. Le solde arrive en centimes.
			 *
			 * @param {Array} invoices
			 * @return {Array} Les mêmes factures, augmentées de leur solde formaté.
			 */
			async formatInvoices(invoices) {
				for(const invoice of invoices) {
					// Le solde est celui de la comptabilité, comme le mouvement auquel on le compare :
					// l'afficher sous le signe de la devise du fournisseur donnerait un montant juste
					// avec la mauvaise monnaie.
					invoice.balance_format = await this.priceFormat(invoice.suppliersocr_balance / 100, invoice.suppliersocr_ledger_currency, true)

					// Ce que le document annonce, pour que l'opérateur retrouve la facture qu'il a sous
					// les yeux. Une facture déjà dans la devise de tenue n'a rien à rappeler.
					invoice.balance_src_format = invoice.suppliersocr_balance_src === null ? null : await this.priceFormat(invoice.suppliersocr_balance_src / 100, invoice.suppliersocr_currency, true)
				}

				return invoices
			},

			/**
			 * Une pièce ne se règle que si elle a un fournisseur à qui imputer l'écriture, qu'elle va
			 * dans le même sens que le mouvement — une dépense solde une facture, un encaissement un
			 * avoir — et qu'elle porte au moins ce que le mouvement porte : au-delà, la différence
			 * partirait au compte courant du fournisseur sans que l'écran le dise.
			 *
			 * @param {Object} invoice
			 * @return {Boolean}
			 */
			canAssign(invoice) {
				const balance = invoice.suppliersocr_balance
				const same_way = balance * this.amount_cents > 0

				return !this.processing && !!invoice.tier && same_way && Math.abs(balance) >= Math.abs(this.amount_cents)
			},

			/**
			 * Écarte une proposition de l'écran. Rien n'est enregistré : le rejet ne vaut que pour le
			 * traitement en cours de ce mouvement, la proposition restant valable à sa réouverture.
			 *
			 * @param {String} family 'invoices' ou 'payments'.
			 * @param {Number} id Identifiant de la proposition écartée.
			 */
			reject(family, id) {
				this.rejected[family].push(id)
			},

			/**
			 * Rapproche le mouvement d'une facture encore due : le règlement qui la solde est créé à
			 * cette occasion, imputé sur ses échéances.
			 */
			async assignInvoice(invoice) {
				this.processing = 'invoice_' + invoice.suppliersocr_id
				await this.assignSupplierBridgeTransaction(this.bridge_transaction.bridgetransaction_id, [invoice.suppliersocr_id], null, null)
				this.processing = null

				this.done()
			},

			async assignPayment(payment) {
				this.processing = 'payment_' + payment.ispayment_id
				await this.assignSupplierBridgeTransaction(this.bridge_transaction.bridgetransaction_id, [], payment.ispayment_id, null)
				this.processing = null

				this.done()
			},

			/**
			 * Le mouvement est rapproché de la facture déposée. Le volet reste ouvert, contrairement aux
			 * autres rapprochements : la facture n'est pas encore lue, et c'est ici que l'utilisateur la
			 * verra arriver puis rejoindra son contrôle. La liste des flux est prévenue pour montrer le
			 * rapprochement sans attendre.
			 */
			onUploaded(file, response) {
				this.deposited = response.retour
				this.successToast('toast.info_save_succes')
				this.watchPendingOcr()
			},

			/**
			 * L'aperçu du fichier est retiré avant le message d'erreur : la zone de dépôt n'accepte
			 * qu'un document, et celui qui vient d'échouer y resterait à en bloquer la place. Le motif
			 * est dit par un toast plutôt que par la zone, qui rend la réponse de l'API telle quelle.
			 */
			onUploadError() {
				this.$refs.dropzone.removeAllFiles()
				this.failureToast('toast.info_save_failed')
			},

			/**
			 * Guette la fin de la lecture optique, d'abord de près puis de plus en plus loin : elle
			 * aboutit le plus souvent en quelques secondes, et interroger toujours au même rythme
			 * chargerait le serveur pour rien sur les documents qui traînent.
			 *
			 * Le guet s'arrête au bout de trente secondes : une lecture qui n'a pas abouti d'ici là ne
			 * le fera pas dans les suivantes, et la facture reste joignable depuis les écrans de suivi.
			 */
			watchPendingOcr() {
				clearTimeout(this.ocr_timer)

				if(this.ocr_watch_started_at === null) {
					this.ocr_watch_started_at = Date.now()
				}

				// Le prochain rechargement est armé s'il tombe encore dans la fenêtre : c'est le départ de
				// la requête qui compte, pas la décision de la programmer.
				if(Date.now() - this.ocr_watch_started_at + this.ocr_delay > 30000) {
					return
				}

				this.ocr_timer = setTimeout(async () => {
					const invoice = await this.getInvoiceSupplierOCR(this.deposited.suppliersocr_id)

					if(invoice) {
						this.deposited = invoice
					}

					if(!invoice || invoice.suppliersocr_ocr_status === 'waiting') {
						this.watchPendingOcr()
					}
				}, this.ocr_delay)

				this.ocr_delay = Math.min(this.ocr_delay * 1.5, 15000)
			},

			quickPreview(pdf_url) {
				this.pdf_url = pdf_url
				this.$refs.modelPreview.show()
			},

			/**
			 * Le mouvement est rapproché : il ne se réaffecte plus, et l'écran n'a plus lieu d'être. La
			 * page qui nous porte décide de la suite.
			 */
			done() {
				this.successToast('toast.info_save_succes')
				this.$emit('done')
			},

			/**
			 * Cherche sur ce que l'utilisateur vient de surligner dans le libellé : surligner un nom de
			 * commerçant ou une référence, c'est déjà demander la facture qui va avec.
			 */
			captureSelection() {
				const selection = window.getSelection().toString().trim()

				if(selection) {
					this.search_value = selection
					this.search()
				}
			}
		},

		components: {
			vueDropzone: vue2Dropzone,
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
	}
</script>
